import {
  generateMatchmakingV2Message,
  MatchmakingV2MessageType,
  type MatchmakingV2CampaignSwitchPayload,
  type MatchmakingV2ErrorPayload,
  type MatchmakingV2MatchAssignedPayload,
  type MatchmakingV2Message,
} from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import type { IRealtimeService } from '../../../services/realtime/IRealtimeService';
import type { GameInstanceSessionRecord, GameInstanceStore } from '../GameInstanceStore';
import type { GameInstanceLaunchManager } from './GameInstanceLaunchManager';

/** MATCH_ENDED reasons emitted by the host (fail-fast reasons let the API requeue). */
export const MATCH_END_REASON_ALL_PLAYERS_LEFT = 'all_players_left';
export const MATCH_END_REASON_INSTANCE_FAILED = 'instance_failed';
export const MATCH_END_REASON_INSTANCE_NOT_READY = 'instance_not_ready';
export const MATCH_END_REASON_INSTANCE_NOT_FOUND = 'instance_not_found';
export const MATCH_END_REASON_INSTANCE_RELEASED = 'instance_released';
export const MATCH_END_REASON_CAMPAIGN_SWITCH = 'campaign_switch';

/** Structural subset of GameInstanceLaunchManager — single-instance teardown only. */
export type InstanceTeardown = Pick<GameInstanceLaunchManager, 'teardownInstance'>;

/**
 * Settled lifecycle states from which a last-player-left disconnect may kick
 * off a restart. A record that is already `restarting`/`launching`/`stopping`/
 * `failed`/`idle`/`displayCreating` must not be poked again — doing so is the
 * host half of the "restart storm" (stacking restarts on an instance that is
 * already cycling).
 */
const RESTARTABLE_ON_DISCONNECT_STATUSES: readonly GameInstanceSessionRecord['status'][] = [
  'ready',
  'occupied',
  'cooldown',
];

export type InstancePeerIdResolver = (gameInstanceIndex: number) => string | undefined;

/** Late-bound resolver for the active android stream id (S1 stale-stream guard). */
export type ActiveAndroidStreamResolver = () => string | undefined;

/**
 * Structural subset of `InstanceRecoveryManager` (android multi-instance host
 * Phase 4) — the restart policy notified when an instance crashes, and
 * cancelled when this manager detaches.
 */
export type InstanceRecoverySink = {
  onInstanceFailed(gameInstanceIndex: number): void;
  cancelAll(reason: string): void;
  /**
   * Intentionally restart one instance (restart-on-player-leave). Marks the
   * record `restarting` and issues the restart without consuming the crash
   * budget; the relaunch echo returns it to `ready`.
   */
  requestIntentionalRestart(gameInstanceIndex: number): void;
};

/**
 * Late-bound resolver for the active campaign's `restartOnDisconnect` flag
 * (HostCampaignConfig, default true). When true, an instance whose last player
 * leaves is restarted for a clean state instead of being reused after cooldown.
 */
export type RestartOnDisconnectResolver = () => boolean;

/**
 * Owns the host side of the pool match loop (pool-match-lifecycle Phase 2):
 *
 * - MATCH_ASSIGNED → records the assignment on the session (contracts stored,
 *   never verified) and answers HOST_MATCH_READY once the instance is `ready`
 *   and its HOST peer exists. Not-ready/failed/unknown instances get a
 *   fail-fast MATCH_ENDED so the API can requeue.
 * - Match end: last player leaving, instance failure, campaign-switch
 *   eviction, or instance release all emit MATCH_ENDED and clear the
 *   assignment. Failure/eviction also tears the instance down (the first
 *   production caller of `GameInstanceLaunchManager.teardownInstance`).
 * - Cooldown: sessions entering `cooldown` get a timer that returns them to
 *   `ready` when `cooldownEndsAt` elapses.
 */
export class MatchLifecycleManager {
  private unsubscribeStore: (() => void) | null = null;
  private readonly pendingMatchReady = new Map<number, string>();
  private readonly cooldownTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private knownMatches = new Map<string, { matchRequestId: string; gameInstanceIndex: number }>();
  private onV2Error: (payload: MatchmakingV2ErrorPayload) => void = () => undefined;
  private recovery: InstanceRecoverySink | null = null;
  private getActiveAndroidStreamId: ActiveAndroidStreamResolver = () => undefined;
  private shouldRestartOnDisconnect: RestartOnDisconnectResolver = () => false;

  constructor(
    private readonly store: GameInstanceStore,
    private readonly realtimeService: IRealtimeService | undefined,
    private readonly launchManager: InstanceTeardown,
    private readonly getHostAuthId: () => string,
    private getInstancePeerId: InstancePeerIdResolver = () => undefined,
  ) {}

  /** Late-bind the instance peer resolver (peer manager wires after construction). */
  public setInstancePeerResolver(resolver: InstancePeerIdResolver): void {
    this.getInstancePeerId = resolver;
  }

  /** Late-bind the API v2-error sink (GameLaunchCoordinator wires report backoff). */
  public setV2ErrorHandler(handler: (payload: MatchmakingV2ErrorPayload) => void): void {
    this.onV2Error = handler;
  }

  /** Late-bind the instance recovery policy (init.ts wires Phase 4). */
  public setInstanceRecovery(recovery: InstanceRecoverySink): void {
    this.recovery = recovery;
  }

  /**
   * Late-bind the active campaign's `restartOnDisconnect` policy (init.ts).
   * When it resolves true, an instance whose last player leaves is restarted
   * (clean state for the next player) instead of reused after cooldown.
   */
  public setRestartOnDisconnectResolver(resolver: RestartOnDisconnectResolver): void {
    this.shouldRestartOnDisconnect = resolver;
  }

  /**
   * Late-bind the active android stream resolver (Phase 8, review fix S1):
   * `instanceClosed` events whose streamId belongs to a previous campaign's
   * still-registered stream are ignored before they can corrupt the new
   * campaign's instance records.
   */
  public setActiveAndroidStreamResolver(resolver: ActiveAndroidStreamResolver): void {
    this.getActiveAndroidStreamId = resolver;
  }

  /** Subscribe to store changes (failure/cooldown/release reconciliation). */
  public attach(): void {
    if (this.unsubscribeStore) return;
    this.unsubscribeStore = this.store.addListener(() => this.reconcile());
  }

  public detach(): void {
    this.unsubscribeStore?.();
    this.unsubscribeStore = null;
    this.recovery?.cancelAll('match lifecycle detached');
    for (const timer of this.cooldownTimers.values()) clearTimeout(timer);
    this.cooldownTimers.clear();
    this.pendingMatchReady.clear();
    this.knownMatches.clear();
  }

  // ========================================
  // Message entry points (matchmakingV2 handler)
  // ========================================

  public handleMatchAssigned(payload: MatchmakingV2MatchAssignedPayload): void {
    Sentry.logger.info(`[MatchLifecycle] MATCH_ASSIGNED received: match=${payload.matchRequestId} instance=${payload.gameInstanceIndex}`);
    const session = this.store.getSessionByIndex(payload.gameInstanceIndex);

    if (!session) {
      Sentry.logger.warn(`[MatchLifecycle] Instance ${payload.gameInstanceIndex} not found — sending MATCH_ENDED`);
      this.sendMatchEnded(payload.matchRequestId, payload.gameInstanceIndex, MATCH_END_REASON_INSTANCE_NOT_FOUND);
      return;
    }
    if (session.status === 'failed' || session.status === 'stopping') {
      Sentry.logger.warn(`[MatchLifecycle] Instance ${payload.gameInstanceIndex} is ${session.status} — sending MATCH_ENDED`);
      this.sendMatchEnded(payload.matchRequestId, payload.gameInstanceIndex, MATCH_END_REASON_INSTANCE_FAILED);
      this.teardownInstance(payload.gameInstanceIndex, MATCH_END_REASON_INSTANCE_FAILED);
      return;
    }
    const alreadyMatched = session.activeMatchRequestId && session.activeMatchRequestId !== payload.matchRequestId;
    if (session.status !== 'ready' || alreadyMatched) {
      Sentry.logger.warn(`[MatchLifecycle] Instance ${payload.gameInstanceIndex} not ready (status=${session.status}, alreadyMatched=${!!alreadyMatched}) — sending MATCH_ENDED`);
      this.sendMatchEnded(payload.matchRequestId, payload.gameInstanceIndex, MATCH_END_REASON_INSTANCE_NOT_READY);
      return;
    }

    Sentry.logger.info(`[MatchLifecycle] Assigning match ${payload.matchRequestId} to session ${session.sessionId}`);
    this.store.assignMatch(session.sessionId, {
      matchRequestId: payload.matchRequestId,
      playerIds: payload.players.map((p) => p.playerId ?? p.displayName),
      playerContracts: payload.playerContracts,
    });
    this.trySendHostMatchReady(session.sessionId);
  }

  /**
   * Campaign switch evicts the affected instance(s): end any active match and
   * tear the instance down. With `gameInstanceIndex` only that instance is
   * evicted; without it the whole machine's instances are.
   */
  public handleCampaignSwitch(payload: MatchmakingV2CampaignSwitchPayload): void {
    const targets = payload.gameInstanceIndex !== undefined
      ? [this.store.getSessionByIndex(payload.gameInstanceIndex)].filter(
          (s): s is GameInstanceSessionRecord => Boolean(s),
        )
      : this.store.getSessions();

    for (const session of targets) {
      if (session.activeMatchRequestId) {
        this.endMatch(session, MATCH_END_REASON_CAMPAIGN_SWITCH, false);
      }
      this.teardownInstance(session.gameInstanceIndex, `campaign_switch:${payload.reason}`);
    }
  }

  /** Called when the API assigns a HOST peer id to an instance. */
  public handleInstancePeerAssigned(gameInstanceIndex: number): void {
    const session = this.store.getSessionByIndex(gameInstanceIndex);
    if (session) this.trySendHostMatchReady(session.sessionId);
  }

  /**
   * API rejection of a host-originated v2 message (e.g. MATCHMAKING_V2_DISABLED
   * while the API flag is off). Forwarded to the coordinator's backoff logic.
   */
  public handleV2Error(payload: MatchmakingV2ErrorPayload): void {
    this.onV2Error(payload);
  }

  /**
   * One instance closed/died (android multi-instance host Phase 4; routed from
   * `processInstanceClosedMSG` via the instanceClosed handler).
   *
   * Crash path: any held match ends (`instance_failed`, no teardown — the
   * recovery policy restarts the instance), the record transitions to
   * `failed`, and the recovery policy is notified. Peer deregistration and the
   * immediate state report follow from the store change (peer manager +
   * report manager subscriptions). Sibling instances are untouched.
   *
   * Expected closes are not crashes: a `restarting` record (intentional or
   * policy restart in flight) skips the failure handling — its held match
   * still ends, since players cannot survive a VM restart. A close for a
   * `stopping`/torn-down/unknown instance is ignored, as is a duplicate close
   * for an already-`failed` one.
   */
  public handleInstanceClosed(event: { streamId: string; gameInstanceIndex: number; reason: string }): void {
    // Phase 8 (review fix S1): a late close from a previous campaign's stream
    // must not corrupt this campaign's instance. Only guarded when an android
    // stream is active — with none active, the unknown/torn-down checks below
    // already drop stale events (and legacy tests run without the resolver).
    const activeStreamId = this.getActiveAndroidStreamId();
    if (activeStreamId !== undefined && event.streamId !== activeStreamId) {
      Sentry.logger.warn(
        `[MatchLifecycleManager] instanceClosed for stale stream "${event.streamId}" ` +
        `(active: "${activeStreamId}", instance ${event.gameInstanceIndex}) — ignoring`,
      );
      return;
    }
    const session = this.store.getSessionByIndex(event.gameInstanceIndex);
    if (!session) {
      Sentry.logger.warn(
        `[MatchLifecycleManager] instanceClosed for unknown/torn-down instance ` +
        `${event.gameInstanceIndex} (stream "${event.streamId}") — ignoring`,
      );
      return;
    }
    if (session.status === 'stopping' || session.status === 'failed') return;
    if (session.status === 'restarting') {
      if (session.activeMatchRequestId) {
        this.endMatch(session, MATCH_END_REASON_INSTANCE_FAILED, false);
      }
      return;
    }

    if (session.activeMatchRequestId) {
      this.endMatch(session, MATCH_END_REASON_INSTANCE_FAILED, false);
    }
    this.store.transition(session.sessionId, 'failed', event.reason);
    this.recovery?.onInstanceFailed(event.gameInstanceIndex);
  }

  // ========================================
  // Occupancy entry points (PeerCoordinator)
  // ========================================

  public playerJoinedSession(sessionId: string): number {
    if (!this.store.getSession(sessionId)) return 0;
    return this.store.recordPlayerConnected(sessionId);
  }

  public playerLeftSession(sessionId: string): number {
    const before = this.store.getSession(sessionId);
    if (!before) return 0;
    const previousPlayerCount = before.playerCount;
    const playerCount = this.store.recordPlayerDisconnected(sessionId);
    // Edge-trigger the restart on the TRUE last-player-left (1 -> 0) only.
    // `recordPlayerDisconnected` floors at 0, so a duplicate/flapping disconnect
    // for a session already at 0 players returns 0 again — firing on that (the
    // old `playerCount === 0` check) re-issued a restart every time and was the
    // trigger for the restart storm. `previousPlayerCount === 1` is the single
    // definitive last-player-left edge.
    if (previousPlayerCount === 1 && playerCount === 0) {
      this.maybeRestartOnLastPlayerLeft(sessionId);
    }
    return playerCount;
  }

  /**
   * Last player left an instance. When the active campaign's
   * `restartOnDisconnect` is on and an android multi-instance stream is live,
   * restart the instance for a clean state (the held match is already ended by
   * `reconcile` on the cooldown transition). Otherwise leave today's
   * cooldown -> ready reuse in place.
   */
  private maybeRestartOnLastPlayerLeft(sessionId: string): void {
    if (!this.shouldRestartOnDisconnect()) return;
    if (this.getActiveAndroidStreamId() === undefined) return;
    const session = this.store.getSession(sessionId);
    if (!session) return;
    // Only restart from a settled, restartable state. If the record is already
    // restarting/launching/stopping/failed, a restart is either in flight or
    // inappropriate — the recovery policy owns it from here.
    if (!RESTARTABLE_ON_DISCONNECT_STATUSES.includes(session.status)) return;
    this.recovery?.requestIntentionalRestart(session.gameInstanceIndex);
  }

  // ========================================
  // Internal — reconciliation on store changes
  // ========================================

  private reconcile(): void {
    // Re-read each session: endMatch clears assignments, which re-enters this
    // listener synchronously — stale snapshots would double-send MATCH_ENDED.
    for (const stale of this.store.getSessions()) {
      const session = this.store.getSession(stale.sessionId);
      if (!session?.activeMatchRequestId) continue;
      if (session.status === 'failed' || session.status === 'stopping') {
        this.endMatch(session, MATCH_END_REASON_INSTANCE_FAILED, true);
      } else if (session.status === 'cooldown' && session.playerCount === 0) {
        this.endMatch(session, MATCH_END_REASON_ALL_PLAYERS_LEFT, false);
      }
    }

    this.detectReleasedMatches();
    this.flushPendingMatchReady();
    this.reconcileCooldownTimers();
  }

  /** A session that vanished while holding a match still owes the API a MATCH_ENDED. */
  private detectReleasedMatches(): void {
    const sessions = this.store.getSessions();
    const liveSessionIds = new Set(sessions.map((s) => s.sessionId));
    const currentMatches = new Map(
      sessions
        .filter((s) => s.activeMatchRequestId)
        .map((s) => [s.sessionId, {
          matchRequestId: s.activeMatchRequestId as string,
          gameInstanceIndex: s.gameInstanceIndex,
        }]),
    );
    for (const [sessionId, info] of this.knownMatches) {
      if (!currentMatches.has(sessionId) && !liveSessionIds.has(sessionId)) {
        this.sendMatchEnded(info.matchRequestId, info.gameInstanceIndex, MATCH_END_REASON_INSTANCE_RELEASED);
      }
    }
    this.knownMatches = currentMatches;
  }

  private flushPendingMatchReady(): void {
    for (const [index, matchRequestId] of [...this.pendingMatchReady]) {
      const session = this.store.getSessionByIndex(index);
      if (!session || session.activeMatchRequestId !== matchRequestId || session.status !== 'ready') {
        this.pendingMatchReady.delete(index);
        continue;
      }
      this.trySendHostMatchReady(session.sessionId);
    }
  }

  private reconcileCooldownTimers(): void {
    for (const session of this.store.getSessions()) {
      if (session.status === 'cooldown' && session.cooldownEndsAt !== undefined) {
        this.ensureCooldownTimer(session.sessionId, session.cooldownEndsAt);
      }
    }
    for (const [sessionId, timer] of [...this.cooldownTimers]) {
      const session = this.store.getSession(sessionId);
      if (!session || session.status !== 'cooldown') {
        clearTimeout(timer);
        this.cooldownTimers.delete(sessionId);
      }
    }
  }

  private ensureCooldownTimer(sessionId: string, cooldownEndsAt: number): void {
    if (this.cooldownTimers.has(sessionId)) return;
    const delay = Math.max(0, cooldownEndsAt - Date.now());
    const timer = setTimeout(() => {
      this.cooldownTimers.delete(sessionId);
      const current = this.store.getSession(sessionId);
      if (current?.status === 'cooldown') {
        this.store.completeCooldown(sessionId);
      }
    }, delay);
    this.cooldownTimers.set(sessionId, timer);
  }

  // ========================================
  // Internal — match lifecycle primitives
  // ========================================

  private endMatch(session: GameInstanceSessionRecord, reason: string, teardown: boolean): void {
    const matchRequestId = session.activeMatchRequestId;
    if (!matchRequestId) return;
    this.pendingMatchReady.delete(session.gameInstanceIndex);
    this.store.clearMatchAssignment(session.sessionId);
    this.sendMatchEnded(matchRequestId, session.gameInstanceIndex, reason);
    if (teardown) this.teardownInstance(session.gameInstanceIndex, reason);
  }

  private trySendHostMatchReady(sessionId: string): void {
    const session = this.store.getSession(sessionId);
    if (!session?.activeMatchRequestId || session.status !== 'ready') {
      Sentry.logger.warn(`[MatchLifecycle] trySendHostMatchReady skipped: session=${sessionId} status=${session?.status} hasMatch=${!!session?.activeMatchRequestId}`);
      return;
    }

    const hostPeerId = this.getInstancePeerId(session.gameInstanceIndex);
    if (!hostPeerId) {
      Sentry.logger.warn(`[MatchLifecycle] No hostPeerId for instance ${session.gameInstanceIndex} — queuing pendingMatchReady (match=${session.activeMatchRequestId})`);
      this.pendingMatchReady.set(session.gameInstanceIndex, session.activeMatchRequestId);
      return;
    }
    this.pendingMatchReady.delete(session.gameInstanceIndex);
    Sentry.logger.info(`[MatchLifecycle] Sending HOST_MATCH_READY: match=${session.activeMatchRequestId} instance=${session.gameInstanceIndex} peerId=${hostPeerId}`);
    this.send(generateMatchmakingV2Message(MatchmakingV2MessageType.MATCHMAKING_V2_HOST_MATCH_READY, {
      matchRequestId: session.activeMatchRequestId,
      gameInstanceIndex: session.gameInstanceIndex,
      hostPeerId,
    }));
  }

  private teardownInstance(gameInstanceIndex: number, reason: string): void {
    void this.launchManager.teardownInstance(gameInstanceIndex, this.getHostAuthId()).catch((error) => {
      Sentry.logger.warn(`[MatchLifecycleManager] teardownInstance failed index=${gameInstanceIndex} reason=${reason}`);
    });
  }

  private sendMatchEnded(matchRequestId: string, gameInstanceIndex: number, reason: string): void {
    this.send(generateMatchmakingV2Message(MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_ENDED, {
      matchRequestId,
      gameInstanceIndex,
      reason,
    }));
  }

  private send(message: MatchmakingV2Message): void {
    if (!this.realtimeService) return;
    this.realtimeService.sendMessage(
      message as unknown as Parameters<IRealtimeService['sendMessage']>[0],
    );
  }
}
